import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  forkJoin,
  catchError,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';

import {
  Meeting,
  MeetingPayload,
  MeetingRecurrencePattern,
} from 'app/models/meetings/meeting.models';
import { AuthService } from 'app/services/auth.service';
import { MeetingService } from 'app/services/module-meetings/meeting.service';
import {
  NotificationsService,
  Subnotification,
} from 'app/services/notifications/notifications.service';
import { environment } from 'environments/environment';

export type AvatarMovement = 'quiet' | 'soft' | 'active';
export type AvatarMotionPreset =
  | 'none'
  | 'float'
  | 'bounce'
  | 'wave'
  | 'shake'
  | 'pulse'
  | 'wobble'
  | 'spin'
  | 'jump'
  | 'dance'
  | 'sing'
  | 'celebrate'
  | 'sleep'
  | 'think'
  | 'walk'
  | 'dodge';
export type AvatarAppearance = 'robot' | 'orb' | 'custom';
export type AvatarSize = 'small' | 'medium' | 'large';
export type AvatarPackState =
  | 'idle'
  | 'walk'
  | 'dance'
  | 'sing'
  | 'sleep'
  | 'think'
  | 'alert'
  | 'celebrate';
export type AvatarHat = 'none' | 'cap' | 'helmet' | 'artist';

export type AvatarCommand =
  | 'TODAY'
  | 'TOMORROW'
  | 'THIS_WEEK'
  | 'NEXT_MEETING'
  | 'MEETINGS'
  | 'CREATE_REMINDER'
  | 'LIST_REMINDERS'
  | 'NOTIFICATIONS'
  | 'OPEN_CALENDAR'
  | 'SETTINGS'
  | 'MUTE'
  | 'HIDE'
  | 'DANCE'
  | 'SING'
  | 'SLEEP'
  | 'CELEBRATE'
  | 'THINK'
  | 'WALK'
  | 'UNKNOWN';

export type AvatarQuickAction =
  | 'TODAY'
  | 'TOMORROW'
  | 'THIS_WEEK'
  | 'NEXT_MEETING'
  | 'CREATE_REMINDER'
  | 'LIST_REMINDERS'
  | 'NOTIFICATIONS'
  | 'OPEN_CALENDAR';

export type AvatarPhraseCategory =
  | 'motivational'
  | 'notification'
  | 'meeting'
  | 'reminder'
  | 'overdue'
  | 'greeting'
  | 'custom';

export type AvatarPhraseReaction =
  | 'idle'
  | 'happy'
  | 'wave'
  | 'alert'
  | 'thinking';

export type AvatarSoundEvent = 'notification' | 'meeting' | 'reminder';

export interface AvatarPhraseEntry {
  id: string;
  text: string;
  reaction: AvatarPhraseReaction;
  enabled: boolean;
}

export type AvatarPhraseLibrary = Record<
  AvatarPhraseCategory,
  AvatarPhraseEntry[]
>;

export interface AvatarPhraseContext {
  title?: string;
  minutes?: number;
  time?: string;
  message?: string;
  platform?: string;
  delay?: string;
}

export interface AvatarRenderedPhrase {
  text: string;
  reaction: AvatarPhraseReaction;
}

export interface AvatarPosition {
  x: number;
  y: number;
}

export interface AvatarMovementProfile {
  idle: AvatarMotionPreset;
  notification: AvatarMotionPreset;
  meeting: AvatarMotionPreset;
  reminder: AvatarMotionPreset;
  motivation: AvatarMotionPreset;
  greeting: AvatarMotionPreset;
  touch: AvatarMotionPreset;
  success: AvatarMotionPreset;
  intensity: number;
  speed: number;
  touchRandom: boolean;
  effectsEnabled: boolean;
  musicalNotesEnabled: boolean;
  confettiEnabled: boolean;
  singSoundEnabled: boolean;
}

export interface AvatarMascotStyle {
  hands: boolean;
  feet: boolean;
  mouth: boolean;
  hat: AvatarHat;
  computer: boolean;
  artist: boolean;
  miniGames: boolean;
  animationPackEnabled: boolean;
}

export interface AvatarAnimationAssetMeta {
  state: AvatarPackState;
  file_name: string;
  mime_type: string;
  file_size: number;
  width: number;
  height: number;
  updated_at?: string;
}

export interface AvatarPreferences {
  enabled: boolean;
  name: string;
  appearance: AvatarAppearance;
  primaryColor: string;
  secondaryColor: string;
  size: AvatarSize;
  movement: AvatarMovement;
  movementProfile: AvatarMovementProfile;
  mascotStyle: AvatarMascotStyle;
  motivationalEnabled: boolean;
  warningEnabled: boolean;
  muted: boolean;
  randomInteractionsEnabled: boolean;
  randomInteractionMinMinutes: number;
  randomInteractionMaxMinutes: number;
  quickActions: AvatarQuickAction[];
  customAvatarDataUrl: string | null;
  hasCustomAvatar: boolean;
  motivationalPhrases: string[];
  warningPhrases: string[];
  phraseLibrary: AvatarPhraseLibrary;
  personalizeNameEnabled: boolean;
  soundEnabled: boolean;
  soundVolume: number;
  soundNotifications: boolean;
  soundMeetings: boolean;
  soundReminders: boolean;
  meetingAlertsEnabled: boolean;
  meetingAlertMinutes: number;
  position: AvatarPosition | null;
}

export interface AvatarReminderInput {
  subject: string;
  description?: string;
  date: string;
  time: string;
  reminderMinutes: number;
  recurrence?: MeetingRecurrencePattern | null;
}

export interface ParsedRecurringReminder {
  subject: string;
  dayOfWeek:
    | 'MONDAY'
    | 'TUESDAY'
    | 'WEDNESDAY'
    | 'THURSDAY'
    | 'FRIDAY'
    | 'SATURDAY'
    | 'SUNDAY';
  nextDate: string;
}

export interface AvatarDailySummary {
  meetings: number;
  reminders: number;
  notifications: number;
  nextMeeting: Meeting | null;
}

interface AvatarPreferenceApi {
  enabled?: boolean;
  assistant_name?: string;
  appearance?: AvatarAppearance;
  primary_color?: string;
  secondary_color?: string;
  size?: AvatarSize;
  movement?: AvatarMovement;
  movement_profile?: Partial<AvatarMovementProfile> & {
    touch_random?: boolean;
    effects_enabled?: boolean;
    musical_notes_enabled?: boolean;
    confetti_enabled?: boolean;
    sing_sound_enabled?: boolean;
  };
  mascot_style?: {
    hands?: boolean;
    feet?: boolean;
    mouth?: boolean;
    hat?: AvatarHat;
    computer?: boolean;
    artist?: boolean;
    mini_games?: boolean;
    animation_pack_enabled?: boolean;
  };
  motivational_enabled?: boolean;
  warning_enabled?: boolean;
  muted?: boolean;
  random_interactions_enabled?: boolean;
  random_interaction_min_minutes?: number;
  random_interaction_max_minutes?: number;
  motivational_phrases?: string[];
  warning_phrases?: string[];
  phrase_library?: Partial<AvatarPhraseLibrary>;
  personalize_name_enabled?: boolean;
  sound_enabled?: boolean;
  sound_volume?: number;
  sound_notifications?: boolean;
  sound_meetings?: boolean;
  sound_reminders?: boolean;
  meeting_alerts_enabled?: boolean;
  meeting_alert_minutes?: number;
  quick_actions?: AvatarQuickAction[];
  position_x?: number | null;
  position_y?: number | null;
  has_custom_avatar?: boolean;
  is_new_profile?: boolean;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const DEFAULT_MOTIVATIONAL_PHRASES = [
  'Buen avance. Continuemos.',
  'Un pendiente menos.',
  'Paso a paso también es progreso.',
  'Organicemos lo importante primero.',
  'Buen momento para revisar tus prioridades.',
  'Vas avanzando. Mantén el foco.',
];

export const DEFAULT_WARNING_PHRASES = [
  'Tienes una novedad pendiente.',
  'Hay algo que requiere tu atención.',
  'Revisa tus próximos compromisos.',
  'Puedo ayudarte a revisar lo que tienes para hoy.',
];

export const DEFAULT_PHRASE_LIBRARY: AvatarPhraseLibrary = {
  motivational: [
    {
      id: 'mot-01',
      text: '¡{nombre}! Buen avance 😄. Sigamos con lo importante.',
      reaction: 'happy',
      enabled: true,
    },
    {
      id: 'mot-02',
      text: '{nombre}, un pendiente menos. Vas bien 👌',
      reaction: 'happy',
      enabled: true,
    },
    {
      id: 'mot-03',
      text: 'Ey {nombre} 👋, buen momento para revisar prioridades.',
      reaction: 'wave',
      enabled: true,
    },
    {
      id: 'mot-04',
      text: '{nombre}, hoy no hace falta correr: solo avanzar bien.',
      reaction: 'happy',
      enabled: true,
    },
    {
      id: 'mot-05',
      text: 'Vamos {nombre} 🚀, una tarea clara a la vez.',
      reaction: 'happy',
      enabled: true,
    },
    {
      id: 'mot-06',
      text: '{nombre}, si ya empezaste, ya vas ganando terreno.',
      reaction: 'happy',
      enabled: true,
    },
    {
      id: 'mot-07',
      text: 'Buen ritmo, {nombre}. Que lo urgente no te quite lo importante.',
      reaction: 'idle',
      enabled: true,
    },
    {
      id: 'mot-08',
      text: 'Ey {nombre} 😄, revisa lo que ya lograste antes de sumar otra tarea.',
      reaction: 'wave',
      enabled: true,
    },
  ],
  notification: [
    {
      id: 'not-01',
      text: '👋 {nombre}, llegó una novedad. ¿La revisamos?',
      reaction: 'wave',
      enabled: true,
    },
    {
      id: 'not-02',
      text: '{nombre}, te llegó esto: {mensaje}',
      reaction: 'alert',
      enabled: true,
    },
    {
      id: 'not-03',
      text: '🔔 {nombre}, hay algo nuevo en ZIVO. Tócame y lo vemos.',
      reaction: 'wave',
      enabled: true,
    },
  ],
  meeting: [
    {
      id: 'meet-01',
      text: '👋 {nombre}, “{titulo}” empieza en {minutos} min.',
      reaction: 'wave',
      enabled: true,
    },
    {
      id: 'meet-02',
      text: '{nombre}, {plataforma} te espera 😄. Faltan {minutos} min para “{titulo}”.',
      reaction: 'happy',
      enabled: true,
    },
    {
      id: 'meet-03',
      text: '{nombre} 👋, faltan {minutos} min para “{titulo}” a las {hora}.',
      reaction: 'wave',
      enabled: true,
    },
    {
      id: 'meet-04',
      text: 'Hora de cambiar de contexto, {nombre}: {plataforma} en {minutos} min.',
      reaction: 'alert',
      enabled: true,
    },
  ],
  reminder: [
    {
      id: 'rem-01',
      text: '⏰ {nombre}, recuerda: {titulo}.',
      reaction: 'alert',
      enabled: true,
    },
    {
      id: 'rem-02',
      text: '{nombre} 👋, tienes pendiente “{titulo}”.',
      reaction: 'wave',
      enabled: true,
    },
    {
      id: 'rem-03',
      text: 'Pequeño toque, {nombre}: “{titulo}” está cerca.',
      reaction: 'alert',
      enabled: true,
    },
    {
      id: 'rem-04',
      text: '{nombre}, no dejes que “{titulo}” se esconda entre pendientes 😄.',
      reaction: 'happy',
      enabled: true,
    },
  ],
  overdue: [
    {
      id: 'overdue-01',
      text: '⏰ {nombre}, esto se te pasó hace {atraso}: “{titulo}”. ¿Lo revisamos?',
      reaction: 'alert',
      enabled: true,
    },
    {
      id: 'overdue-02',
      text: 'Ups {nombre} 😅, quedó pendiente “{titulo}” desde hace {atraso}.',
      reaction: 'wave',
      enabled: true,
    },
    {
      id: 'overdue-03',
      text: '{nombre} 👋, rescato esto antes de que se nos escape: “{titulo}” ({atraso} tarde).',
      reaction: 'happy',
      enabled: true,
    },
  ],
  greeting: [
    {
      id: 'greet-01',
      text: 'Hola {nombre} 👋. ¿Qué revisamos primero?',
      reaction: 'wave',
      enabled: true,
    },
    {
      id: 'greet-02',
      text: '¿Listo {nombre}? Yo vigilo agenda, recordatorios y novedades.',
      reaction: 'happy',
      enabled: true,
    },
    {
      id: 'greet-03',
      text: 'Ey {nombre} 👋, dime “hoy” y te ordeno la agenda.',
      reaction: 'wave',
      enabled: true,
    },
  ],
  custom: [],
};

export const DEFAULT_MOVEMENT_PROFILE: AvatarMovementProfile = {
  idle: 'float',
  notification: 'wave',
  meeting: 'bounce',
  reminder: 'shake',
  motivation: 'jump',
  greeting: 'wave',
  touch: 'dance',
  success: 'celebrate',
  intensity: 7,
  speed: 7,
  touchRandom: true,
  effectsEnabled: true,
  musicalNotesEnabled: true,
  confettiEnabled: true,
  singSoundEnabled: true,
};

export const DEFAULT_MASCOT_STYLE: AvatarMascotStyle = {
  hands: true,
  feet: true,
  mouth: true,
  hat: 'none',
  computer: false,
  artist: false,
  miniGames: true,
  animationPackEnabled: true,
};

export const DEFAULT_QUICK_ACTIONS: AvatarQuickAction[] = [
  'TODAY',
  'TOMORROW',
  'CREATE_REMINDER',
  'NEXT_MEETING',
  'LIST_REMINDERS',
  'NOTIFICATIONS',
];

export const DEFAULT_AVATAR_PREFERENCES: AvatarPreferences = {
  enabled: true,
  name: 'Zivi',
  appearance: 'robot',
  primaryColor: '#17365d',
  secondaryColor: '#4f8fc9',
  size: 'medium',
  movement: 'active',
  movementProfile: { ...DEFAULT_MOVEMENT_PROFILE },
  mascotStyle: { ...DEFAULT_MASCOT_STYLE },
  motivationalEnabled: true,
  warningEnabled: true,
  muted: false,
  randomInteractionsEnabled: true,
  randomInteractionMinMinutes: 2,
  randomInteractionMaxMinutes: 5,
  quickActions: [...DEFAULT_QUICK_ACTIONS],
  customAvatarDataUrl: null,
  hasCustomAvatar: false,
  motivationalPhrases: [...DEFAULT_MOTIVATIONAL_PHRASES],
  warningPhrases: [...DEFAULT_WARNING_PHRASES],
  phraseLibrary: clonePhraseLibrary(DEFAULT_PHRASE_LIBRARY),
  personalizeNameEnabled: true,
  soundEnabled: true,
  soundVolume: 55,
  soundNotifications: true,
  soundMeetings: true,
  soundReminders: true,
  meetingAlertsEnabled: true,
  meetingAlertMinutes: 10,
  position: null,
};

@Injectable({ providedIn: 'root' })
export class AvatarAssistantService {
  private readonly baseUrl = `${environment.apiUrl}/config/avatar-assistant`;
  private readonly preferencesSubject: BehaviorSubject<AvatarPreferences>;
  private readonly animationAssetsSubject =
    new BehaviorSubject<AvatarAnimationAssetMeta[]>([]);
  private activeStorageKey = '';
  private avatarObjectUrl: string | null = null;
  private legacyAvatarDataUrl: string | null = null;
  private notificationAudio: HTMLAudioElement | null = null;
  private soundUnlocked = false;
  private lastSoundAt = 0;
  // Cached client timer; PostgreSQL controls synchronization across tabs and nodes.
  private lastCalendarSyncAt = 0;
  private readonly animationObjectUrls = new Map<AvatarPackState, string>();

  readonly preferences$: Observable<AvatarPreferences>;
  readonly animationAssets$ = this.animationAssetsSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly meetingService: MeetingService,
    private readonly notificationsService: NotificationsService,
  ) {
    this.activeStorageKey = this.storageKey();
    this.legacyAvatarDataUrl = this.readLegacyAvatarDataUrl(
      this.activeStorageKey,
    );
    this.preferencesSubject = new BehaviorSubject<AvatarPreferences>(
      this.loadCachedPreferences(this.activeStorageKey),
    );
    this.preferences$ = this.preferencesSubject.asObservable();
  }

  refreshForCurrentUser(): void {
    const key = this.storageKey();

    if (key !== this.activeStorageKey) {
      this.activeStorageKey = key;
      // Never reuse the previous user's calendar throttle after account switching.
      this.lastCalendarSyncAt = 0;
      this.legacyAvatarDataUrl = this.readLegacyAvatarDataUrl(key);
      this.releaseAvatarObjectUrl();
      for (const state of Array.from(this.animationObjectUrls.keys())) {
        this.releaseAnimationObjectUrl(state);
      }
      this.preferencesSubject.next(this.loadCachedPreferences(key));
    }

    this.syncPreferences().subscribe();
  }

  syncPreferences(): Observable<AvatarPreferences> {
    const cached = this.preferencesSubject.value;

    return this.http
      .get<ApiEnvelope<AvatarPreferenceApi>>(`${this.baseUrl}/preferences`)
      .pipe(
        switchMap((response) => {
          if (response.data.is_new_profile === true) {
            return this.migrateLegacyPreferences(cached);
          }
          return this.hydrateRemotePreferences(response.data);
        }),
        tap((preferences) => this.publishPreferences(preferences)),
        catchError(() => of(this.preferencesSubject.value)),
      );
  }

  getCurrentUserFirstName(): string {
    const user = this.authService.getUsuarioDesdeToken();
    return String(user?.nombre || '').trim().split(/\s+/)[0] || '';
  }

  getSnapshot(): AvatarPreferences {
    return this.preferencesSubject.value;
  }

  savePreferences(
    preferences: AvatarPreferences,
  ): Observable<AvatarPreferences> {
    const optimistic = this.sanitizeLocalPreferences(preferences);
    this.publishPreferences(optimistic);

    return this.http
      .put<ApiEnvelope<AvatarPreferenceApi>>(
        `${this.baseUrl}/preferences`,
        this.toApiPayload(optimistic),
      )
      .pipe(
        map((response) => {
          const mapped = this.mapApiPreferences(
            response.data,
            optimistic.customAvatarDataUrl,
          );
          return {
            ...mapped,
            customAvatarDataUrl: optimistic.customAvatarDataUrl,
          };
        }),
        tap((saved) => this.publishPreferences(saved)),
      );
  }

  updatePreferences(
    changes: Partial<AvatarPreferences>,
  ): AvatarPreferences {
    const next = this.sanitizeLocalPreferences({
      ...this.preferencesSubject.value,
      ...changes,
    });

    this.publishPreferences(next);
    this.savePreferences(next).subscribe({
      error: (error) =>
        console.warn(
          '[AvatarAssistant] No se pudo sincronizar la preferencia.',
          error,
        ),
    });
    return next;
  }

  setPosition(position: AvatarPosition): void {
    this.updatePreferences({ position });
  }

  resetPreferences(): Observable<AvatarPreferences> {
    const current = this.preferencesSubject.value;
    return this.savePreferences({
      ...DEFAULT_AVATAR_PREFERENCES,
      movementProfile: { ...DEFAULT_MOVEMENT_PROFILE },
      mascotStyle: { ...DEFAULT_MASCOT_STYLE },
      phraseLibrary: clonePhraseLibrary(DEFAULT_PHRASE_LIBRARY),
      customAvatarDataUrl: current.customAvatarDataUrl,
      hasCustomAvatar: current.hasCustomAvatar,
    });
  }

  uploadAvatar(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('avatar', file, file.name);

    return this.http
      .post<ApiEnvelope<unknown>>(`${this.baseUrl}/avatar`, formData)
      .pipe(
        switchMap(() => this.loadAvatarObjectUrl()),
        tap((url) => {
          this.publishPreferences({
            ...this.preferencesSubject.value,
            appearance: 'custom',
            hasCustomAvatar: true,
            customAvatarDataUrl: url,
          });
        }),
      );
  }

  deleteAvatar(): Observable<boolean> {
    return this.http
      .delete<{ success: boolean; deleted: boolean }>(
        `${this.baseUrl}/avatar`,
      )
      .pipe(
        map((response) => response.deleted === true),
        tap(() => {
          this.releaseAvatarObjectUrl();
          this.publishPreferences({
            ...this.preferencesSubject.value,
            appearance: 'robot',
            hasCustomAvatar: false,
            customAvatarDataUrl: null,
          });
        }),
      );
  }

  listAnimationAssets(): Observable<AvatarAnimationAssetMeta[]> {
    return this.http
      .get<ApiEnvelope<AvatarAnimationAssetMeta[]>>(
        `${this.baseUrl}/animations`,
      )
      .pipe(
        map((response) => response.data || []),
        tap((assets) => this.animationAssetsSubject.next(assets)),
      );
  }

  getAnimationUrl(state: AvatarPackState): Observable<string | null> {
    const cached = this.animationObjectUrls.get(state);
    if (cached) return of(cached);

    return this.http
      .get(`${this.baseUrl}/animations/${state}`, {
        responseType: 'blob',
      })
      .pipe(
        map((blob) => {
          const url = URL.createObjectURL(blob);
          this.animationObjectUrls.set(state, url);
          return url;
        }),
        catchError(() => of(null)),
      );
  }

  uploadAnimationAsset(
    state: AvatarPackState,
    file: File,
  ): Observable<AvatarAnimationAssetMeta> {
    const formData = new FormData();
    formData.append('animation', file, file.name);

    return this.http
      .post<ApiEnvelope<AvatarAnimationAssetMeta>>(
        `${this.baseUrl}/animations/${state}`,
        formData,
      )
      .pipe(
        map((response) => response.data),
        tap(() => this.releaseAnimationObjectUrl(state)),
      );
  }

  deleteAnimationAsset(state: AvatarPackState): Observable<boolean> {
    return this.http
      .delete<{ success: boolean; deleted: boolean }>(
        `${this.baseUrl}/animations/${state}`,
      )
      .pipe(
        map((response) => response.deleted === true),
        tap(() => this.releaseAnimationObjectUrl(state)),
      );
  }

  shouldShowDailyBrief(kind: 'startup' | 'closing'): boolean {
    const today = new Date().toISOString().slice(0, 10);
    return localStorage.getItem(this.dailyBriefKey(kind)) !== today;
  }

  markDailyBriefShown(kind: 'startup' | 'closing'): void {
    const today = new Date().toISOString().slice(0, 10);
    try {
      localStorage.setItem(this.dailyBriefKey(kind), today);
    } catch {
      // El resumen seguirá funcionando aunque el navegador bloquee storage.
    }
  }

  parseCommand(rawValue: string): AvatarCommand {
    const value = this.normalize(rawValue);
    if (!value) return 'UNKNOWN';

    if (
      /^(hoy|agenda hoy|pendientes hoy)$/.test(value) ||
      /que (tengo|debo hacer) hoy/.test(value) ||
      /agenda.*hoy/.test(value)
    ) {
      return 'TODAY';
    }

    if (
      /^(manana|agenda manana|pendientes manana)$/.test(value) ||
      /que (tengo|debo hacer) manana/.test(value)
    ) {
      return 'TOMORROW';
    }

    if (
      /^(semana|esta semana|agenda semana)$/.test(value) ||
      /que (tengo|debo hacer) esta semana/.test(value)
    ) {
      return 'THIS_WEEK';
    }

    if (/proxima reunion|siguiente reunion/.test(value)) {
      return 'NEXT_MEETING';
    }

    if (/^(reuniones|mis reuniones|reuniones de la semana)$/.test(value)) {
      return 'MEETINGS';
    }

    if (
      /recuerdame|recordarme|crear recordatorio|nuevo recordatorio/.test(value)
    ) {
      return 'CREATE_REMINDER';
    }

    if (
      /mis recordatorios|recordatorios|mis notas|notas pendientes/.test(value)
    ) {
      return 'LIST_REMINDERS';
    }

    if (/novedades|notificaciones|avisos/.test(value)) {
      return 'NOTIFICATIONS';
    }

    if (/^(calendario|abrir calendario|agenda)$/.test(value)) {
      return 'OPEN_CALENDAR';
    }

    if (/configuracion|configurar avatar|personalizar avatar/.test(value)) {
      return 'SETTINGS';
    }

    if (/^(silencio|silenciar|mute)$/.test(value)) {
      return 'MUTE';
    }

    if (/^(ocultar|desactivar avatar)$/.test(value)) {
      return 'HIDE';
    }

    return 'UNKNOWN';
  }

  today(): Observable<Meeting[]> {
    const now = new Date();
    return this.listRange(
      this.startOfDay(now),
      this.endOfDay(now),
      now,
    );
  }

  tomorrow(): Observable<Meeting[]> {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return this.listRange(this.startOfDay(date), this.endOfDay(date));
  }

  thisWeek(): Observable<Meeting[]> {
    const start = this.startOfDay(new Date());
    const end = new Date(start);
    const day = end.getDay();
    const daysUntilSunday = day === 0 ? 0 : 7 - day;
    end.setDate(end.getDate() + daysUntilSunday);
    end.setHours(23, 59, 59, 999);
    return this.listRange(start, end, new Date());
  }

  meetingsThisWeek(): Observable<Meeting[]> {
    return this.thisWeek().pipe(
      map((meetings) =>
        meetings.filter((meeting) => meeting.category !== 'REMINDER'),
      ),
    );
  }

  nextMeeting(): Observable<Meeting | null> {
    const start = new Date();
    const end = new Date(start);
    end.setDate(end.getDate() + 30);

    return this.syncMicrosoftCalendarIfDue(start, end).pipe(
      switchMap(() =>
        this.meetingService.list({
          mine: true,
          active: true,
          start_from: start.toISOString(),
          start_to: end.toISOString(),
          page: 1,
          limit: 50,
          order: 'asc',
        }),
      ),
      map((result) => {
        const now = Date.now();
        return (
          result.meetings
            .filter(
              (meeting) =>
                meeting.status === 'SCHEDULED' &&
                meeting.category !== 'REMINDER' &&
                new Date(meeting.end_at).getTime() >= now,
            )
            .sort(
              (a, b) =>
                new Date(a.start_at).getTime() -
                new Date(b.start_at).getTime(),
            )[0] ?? null
        );
      }),
    );
  }

  upcomingForAlerts(hours = 12): Observable<Meeting[]> {
    const now = Date.now();
    const catchUpStart = now - 24 * 60 * 60 * 1000;
    const start = new Date(catchUpStart);
    const end = new Date();
    end.setHours(end.getHours() + Math.max(1, hours));

    return this.syncMicrosoftCalendarIfDue(start, end).pipe(
      switchMap(() =>
        this.meetingService.list({
          mine: true,
          active: true,
          start_from: start.toISOString(),
          start_to: end.toISOString(),
          page: 1,
          limit: 50,
          order: 'asc',
        }),
      ),
      map((result) =>
        result.meetings
          .filter((meeting) => {
            if (meeting.status !== 'SCHEDULED') return false;

            const startsAt = new Date(meeting.start_at).getTime();
            const endsAt = new Date(meeting.end_at).getTime();
            const isReminder = meeting.category === 'REMINDER';

            return isReminder
              ? startsAt >= catchUpStart
              : endsAt >= now;
          })
          .sort(
            (a, b) =>
              new Date(a.start_at).getTime() -
              new Date(b.start_at).getTime(),
          ),
      ),
    );
  }

  listReminders(): Observable<Meeting[]> {
    const start = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const end = new Date();
    end.setDate(end.getDate() + 90);

    return this.syncMicrosoftCalendarIfDue(start, end).pipe(
      switchMap(() =>
        this.meetingService.list({
          mine: true,
          active: true,
          start_from: start.toISOString(),
          start_to: end.toISOString(),
          page: 1,
          limit: 100,
          order: 'asc',
        }),
      ),
      map((result) =>
        result.meetings
          .filter(
            (meeting) =>
              meeting.category === 'REMINDER' &&
              meeting.status === 'SCHEDULED',
          )
          .sort(
            (a, b) =>
              new Date(a.start_at).getTime() -
              new Date(b.start_at).getTime(),
          ),
      ),
    );
  }

  createReminder(input: AvatarReminderInput): Observable<Meeting> {
    const start = new Date(`${input.date}T${input.time}:00`);
    if (Number.isNaN(start.getTime())) {
      throw new Error('La fecha u hora del recordatorio no es válida.');
    }

    if (start.getTime() < Date.now() - 60 * 1000) {
      throw new Error(
        'Esa hora ya pasó. Elige una hora actual o futura para crear el recordatorio.',
      );
    }

    const end = new Date(start.getTime() + 15 * 60 * 1000);
    const timezone =
      Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Guayaquil';

    const payload: MeetingPayload = {
      subject: input.subject.trim(),
      description: input.description?.trim() || null,
      description_type: 'TEXT',
      start_at: start.toISOString(),
      end_at: end.toISOString(),
      timezone,
      meeting_type: 'PRESENTIAL',
      provider: 'MICROSOFT_OUTLOOK',
      location: null,
      is_all_day: false,
      reminder_minutes: Math.max(Number(input.reminderMinutes || 0), 0),
      category: 'REMINDER',
      recurrence_pattern: input.recurrence || null,
      participants: [],
      links: [],
    };

    return this.meetingService.create(payload);
  }

  completeReminder(meeting: Meeting): Observable<Meeting> {
    if (!meeting.external_id) {
      throw new Error('El recordatorio no tiene identificador válido.');
    }
    return this.meetingService.complete(meeting.external_id);
  }

  snoozeReminder(
    meeting: Meeting,
    delay: number | 'tomorrow',
  ): Observable<Meeting> {
    if (!meeting.external_id) {
      throw new Error('El recordatorio no tiene identificador válido.');
    }

    const start = new Date(meeting.start_at);
    const end = new Date(meeting.end_at);
    const duration = Math.max(
      15 * 60 * 1000,
      end.getTime() - start.getTime(),
    );

    if (delay === 'tomorrow') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(
        start.getHours(),
        start.getMinutes(),
        start.getSeconds(),
        0,
      );
      start.setTime(tomorrow.getTime());
    } else {
      start.setTime(Date.now() + Math.max(1, delay) * 60 * 1000);
    }

    const newEnd = new Date(start.getTime() + duration);
    return this.meetingService.reschedule(meeting.external_id, {
      start_at: start.toISOString(),
      end_at: newEnd.toISOString(),
      timezone:
        meeting.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone ||
        'America/Guayaquil',
    });
  }

  parseRecurringReminder(rawValue: string): ParsedRecurringReminder | null {
    const normalized = this.normalize(rawValue);
    if (!normalized.includes('recuerdame')) return null;

    const days: Array<{
      pattern: RegExp;
      value: ParsedRecurringReminder['dayOfWeek'];
      index: number;
    }> = [
      { pattern: /\blunes\b/, value: 'MONDAY', index: 1 },
      { pattern: /\bmartes\b/, value: 'TUESDAY', index: 2 },
      { pattern: /\bmiercoles\b/, value: 'WEDNESDAY', index: 3 },
      { pattern: /\bjueves\b/, value: 'THURSDAY', index: 4 },
      { pattern: /\bviernes\b/, value: 'FRIDAY', index: 5 },
      { pattern: /\bsabado\b/, value: 'SATURDAY', index: 6 },
      { pattern: /\bdomingo\b/, value: 'SUNDAY', index: 0 },
    ];

    const day = days.find((item) => item.pattern.test(normalized));
    const recurrent =
      /\b(todos|todas|cada)\b/.test(normalized) && Boolean(day);
    if (!recurrent || !day) return null;

    let subject = normalized
      .replace(/^recuerdame\s+/, '')
      .replace(/\b(todos|todas|cada)\s+(los\s+|las\s+)?/, '')
      .replace(day.pattern, '')
      .replace(/\s+/g, ' ')
      .trim();

    if (!subject) subject = 'Recordatorio recurrente';

    const next = new Date();
    const diff = (day.index - next.getDay() + 7) % 7 || 7;
    next.setDate(next.getDate() + diff);

    return {
      subject: subject.charAt(0).toUpperCase() + subject.slice(1),
      dayOfWeek: day.value,
      nextDate: this.dateInputValue(next),
    };
  }

  dailySummary(): Observable<AvatarDailySummary> {
    return forkJoin({
      today: this.today(),
      notifications: this.unreadNotifications(),
      nextMeeting: this.nextMeeting(),
    }).pipe(
      map(({ today, notifications, nextMeeting }) => ({
        meetings: today.filter((meeting) => meeting.category !== 'REMINDER')
          .length,
        reminders: today.filter((meeting) => meeting.category === 'REMINDER')
          .length,
        notifications: notifications.length,
        nextMeeting,
      })),
    );
  }

  closingSummary(): Observable<AvatarDailySummary> {
    return forkJoin({
      tomorrow: this.tomorrow(),
      notifications: this.unreadNotifications(),
    }).pipe(
      map(({ tomorrow, notifications }) => ({
        meetings: tomorrow.filter(
          (meeting) => meeting.category !== 'REMINDER',
        ).length,
        reminders: tomorrow.filter(
          (meeting) => meeting.category === 'REMINDER',
        ).length,
        notifications: notifications.length,
        nextMeeting:
          tomorrow.find((meeting) => meeting.category !== 'REMINDER') || null,
      })),
    );
  }

  unreadNotifications(): Observable<Subnotification[]> {
    return this.notificationsService.getNotificationsPreview('no_leidas');
  }

  getMotivationalPhrase(): string {
    const structured = this.renderPhrase('motivational');
    if (structured) return structured.text;

    const phrases = this.preferencesSubject.value.motivationalPhrases.filter(
      Boolean,
    );
    return this.personalizeText(
      this.randomPhrase(
        phrases.length ? phrases : DEFAULT_MOTIVATIONAL_PHRASES,
      ),
    );
  }

  getWarningPhrase(): string {
    const structured = this.renderPhrase('notification');
    if (structured) return structured.text;

    const phrases = this.preferencesSubject.value.warningPhrases.filter(Boolean);
    return this.personalizeText(
      this.randomPhrase(
        phrases.length ? phrases : DEFAULT_WARNING_PHRASES,
      ),
    );
  }

  renderPhrase(
    category: AvatarPhraseCategory,
    context: AvatarPhraseContext = {},
  ): AvatarRenderedPhrase | null {
    const preferences = this.preferencesSubject.value;
    const structured =
      preferences.phraseLibrary[category]?.filter(
        (item) => item.enabled && item.text.trim(),
      ) || [];

    const simple =
      category === 'motivational'
        ? preferences.motivationalPhrases
        : category === 'notification'
          ? preferences.warningPhrases
          : [];

    const simpleEntries: AvatarPhraseEntry[] = simple
      .map((text, index) => ({
        id: `simple-${category}-${index}`,
        text: String(text || '').trim(),
        reaction:
          category === 'notification'
            ? ('alert' as AvatarPhraseReaction)
            : ('happy' as AvatarPhraseReaction),
        enabled: true,
      }))
      .filter((item) => item.text);

    const defaultTexts =
      category === 'motivational'
        ? new Set(DEFAULT_MOTIVATIONAL_PHRASES)
        : category === 'notification'
          ? new Set(DEFAULT_WARNING_PHRASES)
          : new Set<string>();

    const userSimpleEntries = simpleEntries.filter(
      (item) => !defaultTexts.has(item.text),
    );

    // Si el usuario escribió frases propias, estas reciben prioridad real.
    const candidates = userSimpleEntries.length
      ? [
          ...userSimpleEntries,
          ...userSimpleEntries,
          ...userSimpleEntries,
          ...simpleEntries,
          ...structured,
        ]
      : [...simpleEntries, ...structured];

    if (!candidates.length) return null;

    const selected =
      candidates[Math.floor(Math.random() * candidates.length)];
    return {
      text: this.personalizeText(selected.text, context),
      reaction: selected.reaction,
    };
  }

  renderCasualPhrase(): AvatarRenderedPhrase | null {
    const custom = this.renderPhrase('custom');
    if (custom && Math.random() < 0.45) return custom;

    return this.renderPhrase('motivational') || custom;
  }

  wasAlertedEvent(key: string): boolean {
    const history = this.readAlertHistory();
    return Boolean(history[key]);
  }

  markAlertedEvent(key: string): void {
    const history = this.readAlertHistory();
    history[key] = Date.now();

    const rows = Object.entries(history)
      .sort((left, right) => right[1] - left[1])
      .slice(0, 200);

    try {
      localStorage.setItem(
        this.alertHistoryKey(),
        JSON.stringify(Object.fromEntries(rows)),
      );
    } catch {
      // La deduplicación en memoria del componente sigue activa.
    }
  }

  playSound(
    event: AvatarSoundEvent,
    force = false,
  ): Promise<boolean> {
    const preferences = this.preferencesSubject.value;

    if (!force) {
      if (!preferences.soundEnabled || preferences.muted) {
        return Promise.resolve(false);
      }

      const eventEnabled =
        event === 'notification'
          ? preferences.soundNotifications
          : event === 'meeting'
            ? preferences.soundMeetings
            : preferences.soundReminders;

      if (!eventEnabled) return Promise.resolve(false);
    }

    const now = Date.now();
    if (!force && now - this.lastSoundAt < 650) {
      return Promise.resolve(false);
    }
    this.lastSoundAt = now;

    const audio = this.getNotificationAudio();
    audio.volume = this.clamp(preferences.soundVolume, 0, 100, 55) / 100;
    audio.currentTime = 0;

    return audio
      .play()
      .then(() => {
        this.soundUnlocked = true;
        return true;
      })
      .catch(() => false);
  }

  unlockSound(): void {
    if (this.soundUnlocked) return;

    const audio = this.getNotificationAudio();
    const previousVolume = audio.volume;
    audio.volume = 0;
    audio.currentTime = 0;
    audio
      .play()
      .then(() => {
        audio.pause();
        audio.currentTime = 0;
        audio.volume = previousVolume;
        this.soundUnlocked = true;
      })
      .catch(() => {
        audio.volume = previousVolume;
      });
  }

  private listRange(
    start: Date,
    end: Date,
    notEndedBefore: Date = start,
  ): Observable<Meeting[]> {
    return this.syncMicrosoftCalendarIfDue(start, end).pipe(
      switchMap(() =>
        this.meetingService.list({
          mine: true,
          active: true,
          start_from: start.toISOString(),
          start_to: end.toISOString(),
          page: 1,
          limit: 100,
          order: 'asc',
        }),
      ),
      map((result) =>
        result.meetings
          .filter(
            (meeting) =>
              new Date(meeting.end_at).getTime() >= notEndedBefore.getTime(),
          )
          .sort(
            (a, b) =>
              new Date(a.start_at).getTime() -
              new Date(b.start_at).getTime(),
          ),
      ),
    );
  }

  private syncMicrosoftCalendarIfDue(_start?: Date, _end?: Date): Observable<unknown> {
    const now = Date.now();
    // Cheap local checks stay active; Microsoft sync is a separate 15-minute trigger.
    if (now - this.lastCalendarSyncAt < 15 * 60 * 1000) return of(null);
    this.lastCalendarSyncAt = now;

    return this.http.post(`${this.baseUrl}/calendar/sync`, {}).pipe(
      catchError(() => of(null)),
    );
  }

  private hydrateRemotePreferences(
    row: AvatarPreferenceApi,
  ): Observable<AvatarPreferences> {
    const mapped = this.mapApiPreferences(row);
    if (!mapped.hasCustomAvatar) return of(mapped);

    return this.loadAvatarObjectUrl().pipe(
      map((url) => ({
        ...mapped,
        customAvatarDataUrl: url,
        appearance: url ? mapped.appearance : 'robot',
      })),
      catchError(() =>
        of({
          ...mapped,
          appearance: 'robot' as AvatarAppearance,
          customAvatarDataUrl: null,
          hasCustomAvatar: false,
        }),
      ),
    );
  }

  private migrateLegacyPreferences(
    cached: AvatarPreferences,
  ): Observable<AvatarPreferences> {
    const legacyFile = this.legacyAvatarDataUrl
      ? this.fileFromDataUrl(this.legacyAvatarDataUrl)
      : null;

    if (legacyFile) {
      return this.uploadAvatar(legacyFile).pipe(
        switchMap((url) =>
          this.savePreferences({
            ...cached,
            appearance: 'custom',
            hasCustomAvatar: true,
            customAvatarDataUrl: url,
          }),
        ),
        tap(() => {
          this.legacyAvatarDataUrl = null;
        }),
      );
    }

    const safeCached: AvatarPreferences = {
      ...cached,
      appearance:
        cached.appearance === 'custom' && !cached.hasCustomAvatar
          ? 'robot'
          : cached.appearance,
      customAvatarDataUrl: null,
    };

    return this.savePreferences(safeCached).pipe(
      tap(() => {
        this.legacyAvatarDataUrl = null;
      }),
    );
  }

  private fileFromDataUrl(dataUrl: string): File | null {
    try {
      const match = dataUrl.match(
        /^data:(image\/(?:png|jpeg|webp|gif));base64,(.+)$/i,
      );
      if (!match) return null;

      const mimeType = match[1].toLowerCase();
      const bytes = atob(match[2]);
      const buffer = new Uint8Array(bytes.length);
      for (let index = 0; index < bytes.length; index += 1) {
        buffer[index] = bytes.charCodeAt(index);
      }

      const extension = mimeType === 'image/jpeg' ? 'jpg' : mimeType.split('/')[1];
      return new File([buffer], `avatar-migrado.${extension}`, {
        type: mimeType,
      });
    } catch {
      return null;
    }
  }

  private readLegacyAvatarDataUrl(storageKey: string): string | null {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const stored = JSON.parse(raw) as Partial<AvatarPreferences>;
      const value = stored.customAvatarDataUrl;
      return typeof value === 'string' && value.startsWith('data:image/')
        ? value
        : null;
    } catch {
      return null;
    }
  }

  private loadAvatarObjectUrl(): Observable<string> {
    return this.http
      .get(`${this.baseUrl}/avatar`, { responseType: 'blob' })
      .pipe(
        map((blob) => {
          this.releaseAvatarObjectUrl();
          this.avatarObjectUrl = URL.createObjectURL(blob);
          return this.avatarObjectUrl;
        }),
      );
  }

  private mapApiPreferences(
    row: AvatarPreferenceApi,
    currentAvatarUrl: string | null = this.avatarObjectUrl,
  ): AvatarPreferences {
    return this.sanitizeLocalPreferences({
      enabled: row.enabled !== false,
      name: row.assistant_name || 'Zivi',
      appearance: row.appearance || 'robot',
      primaryColor: row.primary_color || '#17365d',
      secondaryColor: row.secondary_color || '#4f8fc9',
      size: row.size || 'medium',
      movement: row.movement || 'soft',
      movementProfile: this.sanitizeMovementProfile({
        ...(row.movement_profile || {}),
        touchRandom: row.movement_profile?.touch_random,
        effectsEnabled: row.movement_profile?.effects_enabled,
        musicalNotesEnabled: row.movement_profile?.musical_notes_enabled,
        confettiEnabled: row.movement_profile?.confetti_enabled,
        singSoundEnabled: row.movement_profile?.sing_sound_enabled,
      }),
      mascotStyle: this.sanitizeMascotStyle({
        ...(row.mascot_style || {}),
        miniGames: row.mascot_style?.mini_games,
        animationPackEnabled:
          row.mascot_style?.animation_pack_enabled,
      }),
      motivationalEnabled: row.motivational_enabled !== false,
      warningEnabled: row.warning_enabled !== false,
      muted: row.muted === true,
      randomInteractionsEnabled: row.random_interactions_enabled !== false,
      randomInteractionMinMinutes:
        Number(row.random_interaction_min_minutes) || 4,
      randomInteractionMaxMinutes:
        Number(row.random_interaction_max_minutes) || 10,
      quickActions: Array.isArray(row.quick_actions)
        ? row.quick_actions
        : [...DEFAULT_QUICK_ACTIONS],
      customAvatarDataUrl: currentAvatarUrl,
      hasCustomAvatar: row.has_custom_avatar === true,
      motivationalPhrases: Array.isArray(row.motivational_phrases)
        ? row.motivational_phrases
        : [...DEFAULT_MOTIVATIONAL_PHRASES],
      warningPhrases: Array.isArray(row.warning_phrases)
        ? row.warning_phrases
        : [...DEFAULT_WARNING_PHRASES],
      phraseLibrary: this.sanitizePhraseLibrary(row.phrase_library),
      personalizeNameEnabled: row.personalize_name_enabled !== false,
      soundEnabled: row.sound_enabled !== false,
      soundVolume: Number(row.sound_volume ?? 55),
      soundNotifications: row.sound_notifications !== false,
      soundMeetings: row.sound_meetings !== false,
      soundReminders: row.sound_reminders !== false,
      meetingAlertsEnabled: row.meeting_alerts_enabled !== false,
      meetingAlertMinutes: Number(row.meeting_alert_minutes ?? 10),
      position:
        Number.isFinite(row.position_x) && Number.isFinite(row.position_y)
          ? { x: Number(row.position_x), y: Number(row.position_y) }
          : null,
    });
  }

  private toApiPayload(preferences: AvatarPreferences): AvatarPreferenceApi {
    return {
      enabled: preferences.enabled,
      assistant_name: preferences.name,
      appearance: preferences.appearance,
      primary_color: preferences.primaryColor,
      secondary_color: preferences.secondaryColor,
      size: preferences.size,
      movement: preferences.movement,
      movement_profile: {
        ...preferences.movementProfile,
        touch_random: preferences.movementProfile.touchRandom,
        effects_enabled: preferences.movementProfile.effectsEnabled,
        musical_notes_enabled: preferences.movementProfile.musicalNotesEnabled,
        confetti_enabled: preferences.movementProfile.confettiEnabled,
        sing_sound_enabled: preferences.movementProfile.singSoundEnabled,
      },
      mascot_style: {
        hands: preferences.mascotStyle.hands,
        feet: preferences.mascotStyle.feet,
        mouth: preferences.mascotStyle.mouth,
        hat: preferences.mascotStyle.hat,
        computer: preferences.mascotStyle.computer,
        artist: preferences.mascotStyle.artist,
        mini_games: preferences.mascotStyle.miniGames,
        animation_pack_enabled:
          preferences.mascotStyle.animationPackEnabled,
      },
      motivational_enabled: preferences.motivationalEnabled,
      warning_enabled: preferences.warningEnabled,
      muted: preferences.muted,
      random_interactions_enabled: preferences.randomInteractionsEnabled,
      random_interaction_min_minutes:
        preferences.randomInteractionMinMinutes,
      random_interaction_max_minutes:
        preferences.randomInteractionMaxMinutes,
      motivational_phrases: preferences.motivationalPhrases,
      warning_phrases: preferences.warningPhrases,
      phrase_library: preferences.phraseLibrary,
      personalize_name_enabled: preferences.personalizeNameEnabled,
      sound_enabled: preferences.soundEnabled,
      sound_volume: preferences.soundVolume,
      sound_notifications: preferences.soundNotifications,
      sound_meetings: preferences.soundMeetings,
      sound_reminders: preferences.soundReminders,
      meeting_alerts_enabled: preferences.meetingAlertsEnabled,
      meeting_alert_minutes: preferences.meetingAlertMinutes,
      quick_actions: preferences.quickActions,
      position_x: preferences.position?.x ?? null,
      position_y: preferences.position?.y ?? null,
      has_custom_avatar: preferences.hasCustomAvatar,
    };
  }

  private sanitizeLocalPreferences(
    preferences: AvatarPreferences,
  ): AvatarPreferences {
    const quickActions = Array.from(
      new Set(
        (preferences.quickActions || []).filter(
          (action): action is AvatarQuickAction =>
            DEFAULT_ACTION_SET.has(action),
        ),
      ),
    );

    const minMinutes = this.clamp(
      preferences.randomInteractionMinMinutes,
      2,
      120,
      4,
    );
    const maxMinutes = Math.max(
      minMinutes,
      this.clamp(preferences.randomInteractionMaxMinutes, 2, 180, 10),
    );

    return {
      ...DEFAULT_AVATAR_PREFERENCES,
      ...preferences,
      name: String(preferences.name || 'Zivi').trim().slice(0, 30) || 'Zivi',
      motivationalPhrases: this.cleanPhrases(
        preferences.motivationalPhrases,
        DEFAULT_MOTIVATIONAL_PHRASES,
      ),
      warningPhrases: this.cleanPhrases(
        preferences.warningPhrases,
        DEFAULT_WARNING_PHRASES,
      ),
      movementProfile: this.sanitizeMovementProfile(
        preferences.movementProfile,
      ),
      mascotStyle: this.sanitizeMascotStyle(preferences.mascotStyle),
      phraseLibrary: this.sanitizePhraseLibrary(preferences.phraseLibrary),
      quickActions: quickActions.length
        ? quickActions
        : [...DEFAULT_QUICK_ACTIONS],
      randomInteractionMinMinutes: minMinutes,
      randomInteractionMaxMinutes: maxMinutes,
      soundVolume: this.clamp(preferences.soundVolume, 0, 100, 55),
      meetingAlertMinutes: this.clamp(
        preferences.meetingAlertMinutes,
        1,
        120,
        10,
      ),
    };
  }

  private sanitizeMovementProfile(
    profile?: Partial<AvatarMovementProfile>,
  ): AvatarMovementProfile {
    const source = profile && typeof profile === 'object' ? profile : {};
    const preset = (
      value: AvatarMotionPreset | undefined,
      fallback: AvatarMotionPreset,
    ): AvatarMotionPreset =>
      MOVEMENT_PRESETS.has(value as AvatarMotionPreset)
        ? (value as AvatarMotionPreset)
        : fallback;

    return {
      idle: preset(source.idle, DEFAULT_MOVEMENT_PROFILE.idle),
      notification: preset(
        source.notification,
        DEFAULT_MOVEMENT_PROFILE.notification,
      ),
      meeting: preset(source.meeting, DEFAULT_MOVEMENT_PROFILE.meeting),
      reminder: preset(source.reminder, DEFAULT_MOVEMENT_PROFILE.reminder),
      motivation: preset(
        source.motivation,
        DEFAULT_MOVEMENT_PROFILE.motivation,
      ),
      greeting: preset(source.greeting, DEFAULT_MOVEMENT_PROFILE.greeting),
      touch: preset(source.touch, DEFAULT_MOVEMENT_PROFILE.touch),
      success: preset(source.success, DEFAULT_MOVEMENT_PROFILE.success),
      intensity: this.clamp(
        Number(source.intensity),
        1,
        10,
        DEFAULT_MOVEMENT_PROFILE.intensity,
      ),
      speed: this.clamp(
        Number(source.speed),
        1,
        10,
        DEFAULT_MOVEMENT_PROFILE.speed,
      ),
      touchRandom: source.touchRandom !== false,
      effectsEnabled: source.effectsEnabled !== false,
      musicalNotesEnabled: source.musicalNotesEnabled !== false,
      confettiEnabled: source.confettiEnabled !== false,
      singSoundEnabled: source.singSoundEnabled !== false,
    };
  }

  private sanitizeMascotStyle(
    style?: Partial<AvatarMascotStyle>,
  ): AvatarMascotStyle {
    const source = style && typeof style === 'object' ? style : {};
    const hats = new Set<AvatarHat>(['none', 'cap', 'helmet', 'artist']);

    return {
      hands: source.hands !== false,
      feet: source.feet !== false,
      mouth: source.mouth !== false,
      hat: hats.has(source.hat as AvatarHat)
        ? (source.hat as AvatarHat)
        : 'none',
      computer: source.computer === true,
      artist: source.artist === true,
      miniGames: source.miniGames !== false,
      animationPackEnabled: source.animationPackEnabled !== false,
    };
  }

  private sanitizePhraseLibrary(
    library?: Partial<AvatarPhraseLibrary>,
  ): AvatarPhraseLibrary {
    const source = library && typeof library === 'object' ? library : {};
    const result = {} as AvatarPhraseLibrary;

    for (const category of PHRASE_CATEGORIES) {
      const rows: unknown[] = Array.isArray(source[category])
        ? (source[category] as unknown[])
        : DEFAULT_PHRASE_LIBRARY[category];

      result[category] = rows
        .map((item, index) => {
          if (typeof item === 'string') {
            const text = String(item).trim().slice(0, 220);
            if (!text) return null;
            return {
              id: `${category}-${index + 1}`,
              text,
              reaction: defaultReaction(category),
              enabled: true,
            } as AvatarPhraseEntry;
          }

          if (!item || typeof item !== 'object') return null;
          const row = item as Partial<AvatarPhraseEntry>;
          const text = String(row.text || '').trim().slice(0, 220);
          if (!text) return null;
          const reaction = PHRASE_REACTIONS.has(
            row.reaction as AvatarPhraseReaction,
          )
            ? (row.reaction as AvatarPhraseReaction)
            : defaultReaction(category);

          return {
            id:
              String(row.id || `${category}-${index + 1}`)
                .trim()
                .slice(0, 60) || `${category}-${index + 1}`,
            text,
            reaction,
            enabled: row.enabled !== false,
          } as AvatarPhraseEntry;
        })
        .filter((item): item is AvatarPhraseEntry => Boolean(item))
        .slice(0, 30);
    }

    return result;
  }

  private publishPreferences(preferences: AvatarPreferences): void {
    const sanitized = this.sanitizeLocalPreferences(preferences);
    this.preferencesSubject.next(sanitized);
    this.persistCache(sanitized);

    if (this.notificationAudio) {
      this.notificationAudio.volume = sanitized.soundVolume / 100;
    }
  }

  private personalizeText(
    text: string,
    context: AvatarPhraseContext = {},
  ): string {
    const preferences = this.preferencesSubject.value;
    const user = this.authService.getUsuarioDesdeToken();
    const firstName = String(user?.nombre || '').trim().split(/\s+/)[0] || '';
    const title = String(context.title || '').trim();
    const minutes =
      context.minutes === undefined ? '' : String(Math.max(0, context.minutes));
    const time = String(context.time || '').trim();
    const message = String(context.message || '').trim();
    const platform = String(context.platform || '').trim();
    const delay = String(context.delay || '').trim();

    const hadNamePlaceholder = text.includes('{nombre}');
    let rendered = String(text || '')
      .replace(/\{nombre\}/g, firstName || 'amigo')
      .replace(/\{asistente\}/g, preferences.name || 'Zivi')
      .replace(/\{titulo\}/g, title || 'tu compromiso')
      .replace(/\{minutos\}/g, minutes || 'unos')
      .replace(/\{hora\}/g, time || '')
      .replace(/\{mensaje\}/g, message || 'una nueva notificación')
      .replace(/\{plataforma\}/g, platform || 'tu reunión')
      .replace(/\{atraso\}/g, delay || 'un momento')
      .replace(/\s+/g, ' ')
      .trim();

    if (
      preferences.personalizeNameEnabled &&
      firstName &&
      !hadNamePlaceholder &&
      rendered
    ) {
      rendered = `${firstName}, ${rendered.charAt(0).toLowerCase()}${rendered.slice(1)}`;
    }

    return rendered;
  }

  private getNotificationAudio(): HTMLAudioElement {
    if (!this.notificationAudio) {
      this.notificationAudio = new Audio('assets/sounds/notification.mp3');
      this.notificationAudio.preload = 'auto';
      this.notificationAudio.volume =
        this.preferencesSubject.value.soundVolume / 100;
    }
    return this.notificationAudio;
  }

  private startOfDay(date: Date): Date {
    const value = new Date(date);
    value.setHours(0, 0, 0, 0);
    return value;
  }

  private endOfDay(date: Date): Date {
    const value = new Date(date);
    value.setHours(23, 59, 59, 999);
    return value;
  }

  private dateInputValue(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private normalize(value: string): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[¿?¡!.,;:]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private randomPhrase(phrases: string[]): string {
    const index = Math.floor(Math.random() * phrases.length);
    return phrases[index] || '';
  }

  private cleanPhrases(values: string[], defaults: string[]): string[] {
    const phrases = Array.from(
      new Set(
        (Array.isArray(values) ? values : [])
          .map((item) => String(item || '').trim())
          .filter(Boolean)
          .map((item) => item.slice(0, 180)),
      ),
    ).slice(0, 40);

    return phrases.length ? phrases : [...defaults];
  }

  private clamp(
    value: number,
    min: number,
    max: number,
    fallback: number,
  ): number {
    const number = Number(value);
    if (!Number.isFinite(number)) return fallback;
    return Math.max(min, Math.min(max, Math.round(number)));
  }

  private alertHistoryKey(): string {
    const userId = this.authService.getUsuarioDesdeToken()?.id ?? 'anonymous';
    return `zivo_avatar_alerted_events_${userId}`;
  }

  private readAlertHistory(): Record<string, number> {
    const retentionMs = 48 * 60 * 60 * 1000;
    const now = Date.now();

    try {
      const raw = localStorage.getItem(this.alertHistoryKey());
      if (!raw) return {};

      const parsed = JSON.parse(raw) as Record<string, number>;
      return Object.fromEntries(
        Object.entries(parsed || {}).filter(
          ([, timestamp]) =>
            Number.isFinite(Number(timestamp)) &&
            now - Number(timestamp) <= retentionMs,
        ),
      );
    } catch {
      return {};
    }
  }

  private storageKey(): string {
    const userId = this.authService.getUsuarioDesdeToken()?.id ?? 'anonymous';
    return `zivo_avatar_assistant_${userId}`;
  }

  private loadCachedPreferences(storageKey: string): AvatarPreferences {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return this.defaultPreferences();

      const stored = JSON.parse(raw) as Partial<AvatarPreferences>;
      const legacyAvatar =
        this.legacyAvatarDataUrl || this.readLegacyAvatarDataUrl(storageKey);
      return this.sanitizeLocalPreferences({
        ...this.defaultPreferences(),
        ...stored,
        customAvatarDataUrl: legacyAvatar,
        hasCustomAvatar:
          stored.hasCustomAvatar === true || Boolean(legacyAvatar),
      });
    } catch {
      return this.defaultPreferences();
    }
  }

  private defaultPreferences(): AvatarPreferences {
    return {
      ...DEFAULT_AVATAR_PREFERENCES,
      quickActions: [...DEFAULT_QUICK_ACTIONS],
      motivationalPhrases: [...DEFAULT_MOTIVATIONAL_PHRASES],
      warningPhrases: [...DEFAULT_WARNING_PHRASES],
      phraseLibrary: clonePhraseLibrary(DEFAULT_PHRASE_LIBRARY),
      movementProfile: { ...DEFAULT_MOVEMENT_PROFILE },
      mascotStyle: { ...DEFAULT_MASCOT_STYLE },
      position: null,
    };
  }

  private persistCache(preferences: AvatarPreferences): void {
    try {
      const cached = {
        ...preferences,
        customAvatarDataUrl: null,
      };
      localStorage.setItem(
        this.activeStorageKey || this.storageKey(),
        JSON.stringify(cached),
      );
    } catch (error) {
      console.warn('[AvatarAssistant] No se pudo actualizar el cache local.', error);
    }
  }

  private releaseAnimationObjectUrl(state: AvatarPackState): void {
    const current = this.animationObjectUrls.get(state);
    if (!current) return;
    URL.revokeObjectURL(current);
    this.animationObjectUrls.delete(state);
  }

  private dailyBriefKey(kind: 'startup' | 'closing'): string {
    const userId = this.authService.getUsuarioDesdeToken()?.id ?? 'anonymous';
    return `zivo_avatar_brief_${kind}_${userId}`;
  }

  private releaseAvatarObjectUrl(): void {
    if (!this.avatarObjectUrl) return;
    URL.revokeObjectURL(this.avatarObjectUrl);
    this.avatarObjectUrl = null;
  }
}

const PHRASE_CATEGORIES: AvatarPhraseCategory[] = [
  'motivational',
  'notification',
  'meeting',
  'reminder',
  'overdue',
  'greeting',
  'custom',
];

const PHRASE_REACTIONS = new Set<AvatarPhraseReaction>([
  'idle',
  'happy',
  'wave',
  'alert',
  'thinking',
]);

const MOVEMENT_PRESETS = new Set<AvatarMotionPreset>([
  'none',
  'float',
  'bounce',
  'wave',
  'shake',
  'pulse',
  'wobble',
  'spin',
  'jump',
  'dance',
  'sing',
  'celebrate',
  'sleep',
  'think',
  'walk',
  'dodge',
]);

const DEFAULT_ACTION_SET = new Set<AvatarQuickAction>([
  'TODAY',
  'TOMORROW',
  'THIS_WEEK',
  'NEXT_MEETING',
  'CREATE_REMINDER',
  'LIST_REMINDERS',
  'NOTIFICATIONS',
  'OPEN_CALENDAR',
]);

function defaultReaction(
  category: AvatarPhraseCategory,
): AvatarPhraseReaction {
  if (
    category === 'notification' ||
    category === 'reminder' ||
    category === 'overdue'
  ) {
    return 'alert';
  }
  if (category === 'meeting' || category === 'greeting') return 'wave';
  if (category === 'motivational') return 'happy';
  return 'idle';
}

function clonePhraseLibrary(
  library: AvatarPhraseLibrary,
): AvatarPhraseLibrary {
  return {
    motivational: library.motivational.map((item) => ({ ...item })),
    notification: library.notification.map((item) => ({ ...item })),
    meeting: library.meeting.map((item) => ({ ...item })),
    reminder: library.reminder.map((item) => ({ ...item })),
    overdue: library.overdue.map((item) => ({ ...item })),
    greeting: library.greeting.map((item) => ({ ...item })),
    custom: library.custom.map((item) => ({ ...item })),
  };
}
